<template>
  <v-container style="max-width: 1700px" fluid class="pa-4">
    <v-row class="mb-4">
      <v-col>
        <h1 class="text-h4 font-weight-bold white--text">Unternehmensplanung</h1>
      </v-col>
    </v-row>

    <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-6" dark>
      <v-tab>Gesamt (Master-Plan)</v-tab>
      <v-tab>Standort-Planung</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" class="transparent">
      <v-tab-item>
        <v-card outlined class="mb-4" dark>
          <v-card-text>
            <v-row align="center">
              <v-col cols="12" sm="4">
                <v-select
                  v-model="masterYear"
                  :items="availableYears"
                  label="Planungsjahr wählen"
                  outlined
                  dense
                  hide-details
                  dark
                  @change="loadAllPlans"></v-select>
              </v-col>
              <v-col>
                <v-btn color="primary" depressed @click="loadAllPlans"> <v-icon left>mdi-refresh</v-icon> Daten laden </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-row v-if="aggregatedPlan">
          <v-col cols="12" md="4">
            <v-card color="primary" dark>
              <v-card-title>Total Umsatz (Jahreswert)</v-card-title>
              <v-card-text class="text-h4">CHF {{ formatNumber(aggregatedPlan.totalRevenue) }}</v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card color="error" dark>
              <v-card-title>Total Kosten (Jahreswert)</v-card-title>
              <v-card-text class="text-h4">CHF {{ formatNumber(aggregatedPlan.totalCosts) }}</v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card color="success" dark>
              <v-card-title>Total Zugänge (Jahr)</v-card-title>
              <v-card-text class="text-h4">{{ formatNumber(aggregatedPlan.totalJoined) }}</v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <v-tab-item>
        <v-card outlined class="mb-4" dark>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="planData.studioId"
                  :items="mappedStudios"
                  item-text="name"
                  item-value="_id"
                  label="Studio auswählen"
                  outlined
                  dense
                  dark></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select v-model="planData.year" :items="availableYears" label="Planungsjahr" outlined dense dark></v-select>
              </v-col>
              <v-col cols="12" md="3" class="d-flex align-center">
                <v-btn color="primary" depressed @click="loadLocationPlan" :disabled="!planData.studioId || !planData.year">
                  <v-icon left>mdi-cloud-download</v-icon> Plan abrufen
                </v-btn>
              </v-col>
              <v-col cols="12" md="3" class="d-flex align-center justify-end" v-if="isEditing">
                <v-switch
                  v-model="viewMode"
                  true-value="calculated"
                  false-value="input"
                  label="Berechnete Ansicht"
                  color="success"
                  dark
                  hide-details></v-switch>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <div v-if="isEditing && viewMode === 'input'">
          <v-card outlined class="mb-6" dark>
            <v-card-title>Mitglieder-Planung</v-card-title>
            <v-card-text>
              <v-row class="mb-4">
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.number="planData.members.baseJan"
                    label="Stand Anfang Januar"
                    type="number"
                    outlined
                    dense
                    dark></v-text-field>
                </v-col>
              </v-row>

              <v-simple-table dark class="transparent-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Kategorie</th>
                      <th v-for="month in months" :key="month.key">{{ month.label }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style="min-width: 100px" class="font-weight-bold">Zugänge</td>
                      <td v-for="month in months" :key="'join-' + month.key">
                        <v-text-field
                          v-model.number="planData.members.joined[month.key]"
                          type="number"
                          dense
                          hide-details
                          outlined
                          dark
                          class="compact-input"></v-text-field>
                      </td>
                    </tr>
                    <tr>
                      <td style="min-width: 100px" class="font-weight-bold">Abgänge</td>
                      <td v-for="month in months" :key="'left-' + month.key">
                        <v-text-field
                          v-model.number="planData.members.left[month.key]"
                          type="number"
                          dense
                          hide-details
                          outlined
                          dark
                          class="compact-input"></v-text-field>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold" style="min-width: 100px">Ruhend (%)</td>
                      <td v-for="month in months" :key="'pause-' + month.key">
                        <v-text-field
                          v-model.number="planData.members.pausedRate[month.key]"
                          type="number"
                          step="0.1"
                          dense
                          hide-details
                          outlined
                          dark
                          class="compact-input"></v-text-field>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>

          <v-row>
            <v-col cols="12" md="6">
              <v-card outlined class="fill-height" dark>
                <v-card-title>Umsatz-Planung (Werte pro Mitglied / pro Monat)</v-card-title>
                <v-card-text>
                  <v-simple-table dense dark class="transparent-table">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">Position</th>
                          <th class="text-right" style="width: 120px">Betrag</th>
                          <th class="text-right" style="width: 120px">Steig. p.a. (%)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in revenueItems" :key="item.key">
                          <td class="text-left" style="cursor: pointer" @click="openMonthlyDialog('revenue', item)">
                            {{ item.label }}
                            <v-icon
                              v-if="hasExtraMonthly('revenue', item)"
                              small
                              color="info"
                              class="ml-1"
                              title="Zusätzliche Monatsbeträge hinterlegt">
                              mdi-calendar-plus
                            </v-icon>
                          </td>
                          <td>
                            <v-text-field
                              v-model.number="planData.revenue[item.key]"
                              type="number"
                              dense
                              hide-details
                              outlined
                              dark
                              class="compact-input my-1"></v-text-field>
                          </td>
                          <td>
                            <v-text-field
                              v-model.number="planData.revenue.growthRates[item.key]"
                              type="number"
                              step="0.1"
                              dense
                              hide-details
                              outlined
                              dark
                              class="compact-input my-1"></v-text-field>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-card outlined class="fill-height" dark>
                <v-card-title>Kosten-Planung (Werte pro Monat)</v-card-title>
                <v-card-text>
                  <v-simple-table dense dark class="transparent-table">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">Position</th>
                          <th class="text-right" style="width: 120px">Betrag</th>
                          <th class="text-right" style="width: 120px">Steig. p.a. (%)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in costItems" :key="item.key">
                          <td class="text-left" style="cursor: pointer" @click="openMonthlyDialog('costs', item)">
                            {{ item.label }}
                            <v-icon
                              v-if="hasExtraMonthly('costs', item)"
                              small
                              color="info"
                              class="ml-1"
                              title="Zusätzliche Monatsbeträge hinterlegt">
                              mdi-calendar-plus
                            </v-icon>
                          </td>
                          <td>
                            <v-text-field
                              v-if="item.isNested"
                              v-model.number="planData.costs[item.isNested][item.nestedKey]"
                              type="number"
                              dense
                              hide-details
                              outlined
                              dark
                              class="compact-input my-1"></v-text-field>
                            <v-text-field
                              v-else
                              v-model.number="planData.costs[item.key]"
                              type="number"
                              dense
                              hide-details
                              outlined
                              dark
                              class="compact-input my-1"></v-text-field>
                          </td>
                          <td>
                            <v-text-field
                              v-if="item.isNested"
                              v-model.number="planData.costs.growthRates[item.isNested][item.nestedKey]"
                              type="number"
                              step="0.1"
                              dense
                              hide-details
                              outlined
                              dark
                              class="compact-input my-1"></v-text-field>
                            <v-text-field
                              v-else
                              v-model.number="planData.costs.growthRates[item.key]"
                              type="number"
                              step="0.1"
                              dense
                              hide-details
                              outlined
                              dark
                              class="compact-input my-1"></v-text-field>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mt-4">
            <v-col class="text-right">
              <v-btn color="success" large depressed @click="savePlan" :loading="isSaving">
                <v-icon left>mdi-content-save</v-icon> Plan speichern
              </v-btn>
            </v-col>
          </v-row>

          <v-dialog v-model="editMonthlyDialog.isOpen" max-width="600px">
            <v-card dark>
              <v-card-title>
                Zusätzliche Monatsbeträge erfassen
                <v-spacer></v-spacer>
                <v-btn icon @click="editMonthlyDialog.isOpen = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-subtitle>Position: {{ editMonthlyDialog.label }}</v-card-subtitle>
              <v-card-text>
                <p class="text-body-2 mb-4">
                  Hier kannst du für jeden Monat spezifische Zusatzbeträge erfassen, die auf den Basis-Jahreswert / Monatsdurchschnitt aufaddiert
                  werden. Dies ist optional.
                </p>
                <v-row>
                  <v-col cols="4" sm="3" v-for="month in months" :key="month.key">
                    <v-text-field v-model.number="getEditTarget()[month.key]" :label="month.label" type="number" dense outlined dark></v-text-field>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" text @click="editMonthlyDialog.isOpen = false">Fertig</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>

        <!-- BERECHNETE ANSICHT -->
        <div v-else-if="isEditing && viewMode === 'calculated'" class="calculated-view">
          <v-card outlined class="mb-6 calculated-card" dark>
            <v-simple-table dense dark class="calc-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left font-weight-bold header-col primary--text text--lighten-1" style="font-size: 14px !important">
                      Kosten-Planung (Testing)
                    </th>
                    <th class="text-right info-col" width="100">pro Monat</th>
                    <th class="text-right info-col" width="100">Steigerung p.a. in %</th>
                    <th class="text-right info-col" width="100">% vom Umsatz</th>
                    <th class="text-right info-col annual-col" width="100">Jahreswert</th>
                    <th v-for="m in months" :key="m.key" class="text-right" width="80">{{ m.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <template>
                    <tr>
                      <td class="header-col pl-4">{{ costItems.find((c) => c.key === "cogs").label }}</td>
                      <td class="text-right info-col">{{ formatMoney(calculatedData.costsMonthly["cogs"]) }}</td>
                      <td class="text-right info-col">
                        {{
                          formatPercent(
                            (planData.costs.growthRates && planData.costs.growthRates["cogs"] ? planData.costs.growthRates["cogs"] : 0) / 100,
                          )
                        }}
                      </td>
                      <td class="text-right info-col">
                        {{ formatPercent(calculatedData.costsYearly["cogs"] / calculatedData.kpi.yearlyTotalRevenue) }}
                      </td>
                      <td class="text-right info-col annual-col">{{ formatMoney(calculatedData.costsYearly["cogs"]) }}</td>
                      <td v-for="(val, i) in calculatedData.costs['cogs']" :key="'cogs-' + i" class="text-right">{{ formatMoney(val) }}</td>
                    </tr>
                    <tr v-if="actualData" class="ist-row">
                      <td class="header-col pl-6 grey--text text--lighten-1" style="font-size: 11px !important">↳ IST</td>
                      <td class="text-right info-col"></td>
                      <td class="text-right info-col"></td>
                      <td class="text-right info-col"></td>
                      <td class="text-right info-col annual-col grey--text text--lighten-1 font-weight-bold">
                        {{ formatMoney(calculatedActuals.costsYearly["cogs"]) }}
                        <span
                          :class="getDeltaClass(calculatedActuals.costsYearly['cogs'], calculatedData.costsYearly['cogs'])"
                          class="ml-1 text-caption">
                          {{ formatDelta(calculatedActuals.costsYearly["cogs"], calculatedData.costsYearly["cogs"]) }}
                        </span>
                      </td>
                      <td v-for="(val, i) in calculatedActuals.costs['cogs']" :key="'cogs-ist-' + i" class="text-right grey--text text--lighten-1">
                        {{ formatMoney(val) }}
                        <div :class="getDeltaClass(val, calculatedData.costs['cogs'][i])" class="text-caption" style="line-height: 1">
                          {{ formatDelta(val, calculatedData.costs["cogs"][i]) }}
                        </div>
                      </td>
                    </tr>
                  </template>

                  <tr class="font-weight-bold highlight-row">
                    <td class="header-col pl-4">Rohertrag</td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col annual-col">
                      {{ formatMoney(calculatedData.kpi.yearlyTotalRevenue - calculatedData.costsYearly["cogs"]) }}
                    </td>
                    <td v-for="(val, i) in calculatedData.kpi.rohertrag" :key="'rohe-' + i" class="text-right">{{ formatMoney(val) }}</td>
                  </tr>

                  <template v-for="item in opexItems">
                    <tr :key="'opex-' + item.key">
                      <td class="header-col pl-4">{{ item.label }}</td>
                      <td class="text-right info-col">{{ formatMoney(calculatedData.costsMonthly[item.key]) }}</td>
                      <td class="text-right info-col">
                        {{
                          formatPercent(
                            ((planData.costs.growthRates &&
                              (item.isNested ? planData.costs.growthRates[item.isNested][item.nestedKey] : planData.costs.growthRates[item.key])) ||
                              0) / 100,
                          )
                        }}
                      </td>
                      <td class="text-right info-col">
                        {{ formatPercent(calculatedData.costsYearly[item.key] / calculatedData.kpi.yearlyTotalRevenue) }}
                      </td>
                      <td class="text-right info-col annual-col">{{ formatMoney(calculatedData.costsYearly[item.key]) }}</td>
                      <td v-for="(val, i) in calculatedData.costs[item.key]" :key="'opex-val-' + i" class="text-right">{{ formatMoney(val) }}</td>
                    </tr>
                    <tr v-if="actualData" :key="'opex-ist-' + item.key" class="ist-row">
                      <td class="header-col pl-6 grey--text text--lighten-1" style="font-size: 11px !important">↳ IST</td>
                      <td class="text-right info-col"></td>
                      <td class="text-right info-col"></td>
                      <td class="text-right info-col"></td>
                      <td class="text-right info-col annual-col grey--text text--lighten-1 font-weight-bold">
                        {{ formatMoney(calculatedActuals.costsYearly[item.key]) }}
                        <span
                          :class="getDeltaClass(calculatedActuals.costsYearly[item.key], calculatedData.costsYearly[item.key])"
                          class="ml-1 text-caption">
                          {{ formatDelta(calculatedActuals.costsYearly[item.key], calculatedData.costsYearly[item.key]) }}
                        </span>
                      </td>
                      <td
                        v-for="(val, i) in calculatedActuals.costs[item.key]"
                        :key="'opex-val-ist-' + i"
                        class="text-right grey--text text--lighten-1">
                        {{ formatMoney(val) }}
                        <div :class="getDeltaClass(val, calculatedData.costs[item.key][i])" class="text-caption" style="line-height: 1">
                          {{ formatDelta(val, calculatedData.costs[item.key][i]) }}
                        </div>
                      </td>
                    </tr>
                  </template>

                  <template>
                    <tr class="font-weight-bold highlight-row text-error">
                      <td class="header-col pl-4">Gesamtkosten (PLAN)</td>
                      <td class="text-right info-col"></td>
                      <td class="text-right info-col"></td>
                      <td class="text-right info-col">{{ formatPercent(calculatedData.kpi.yearlyOpex / calculatedData.kpi.yearlyTotalRevenue) }}</td>
                      <td class="text-right info-col annual-col">{{ formatMoney(calculatedData.kpi.yearlyOpex) }}</td>
                      <td v-for="(val, i) in calculatedData.kpi.gesamtkosten" :key="'tot-cost-' + i" class="text-right">{{ formatMoney(val) }}</td>
                    </tr>
                    <tr v-if="actualData" class="font-weight-bold highlight-row grey--text text--lighten-1">
                      <td class="header-col pl-6" style="font-size: 12px !important">↳ IST Gesamtkosten</td>
                      <td class="text-right info-col"></td>
                      <td class="text-right info-col"></td>
                      <td class="text-right info-col"></td>
                      <td class="text-right info-col annual-col">
                        {{ formatMoney(calculatedActuals.kpi.yearlyOpex) }}
                        <span :class="getDeltaClass(calculatedActuals.kpi.yearlyOpex, calculatedData.kpi.yearlyOpex)" class="ml-1 text-caption">
                          {{ formatDelta(calculatedActuals.kpi.yearlyOpex, calculatedData.kpi.yearlyOpex) }}
                        </span>
                      </td>
                      <td v-for="(val, i) in calculatedActuals.kpi.gesamtkosten" :key="'tot-cost-ist-' + i" class="text-right">
                        {{ formatMoney(val) }}
                        <div :class="getDeltaClass(val, calculatedData.kpi.gesamtkosten[i])" class="text-caption" style="line-height: 1">
                          {{ formatDelta(val, calculatedData.kpi.gesamtkosten[i]) }}
                        </div>
                      </td>
                    </tr>
                  </template>

                  <tr class="font-weight-black highlight-row total-row">
                    <td class="header-col pl-4">EBIT DA</td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col annual-col" :class="calculatedData.kpi.yearlyEbitda < 0 ? 'text-error' : ''">
                      {{ formatMoney(calculatedData.kpi.yearlyEbitda) }}
                    </td>
                    <td v-for="(val, i) in calculatedData.kpi.ebitda" :key="'ebitda-' + i" class="text-right" :class="val < 0 ? 'text-error' : ''">
                      {{ formatMoney(val) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
          <!-- Mitglieder-Planung -->
          <v-card outlined class="mb-6 calculated-card" dark>
            <v-card-title class="primary white--text font-weight-bold">Mitglieder-Planung</v-card-title>
            <v-simple-table dense dark class="calc-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left font-weight-bold header-col primary--text text--lighten-1" style="font-size: 14px !important">Bereich</th>
                    <th class="text-right info-col annual-col">Jahreswert / Ø</th>
                    <th v-for="m in months" :key="m.key" class="text-right">{{ m.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="header-col pl-4">Stand Anfang des Monats</td>
                    <td class="text-right info-col annual-col"></td>
                    <td v-for="(m, i) in calculatedData.members" :key="'start-' + i" class="text-right">{{ formatMoney(m.start) }}</td>
                  </tr>
                  <tr>
                    <td class="header-col pl-4 text-success">Zugänge (vertragswirksam)</td>
                    <td class="text-right info-col text-success annual-col">{{ formatMoney(calculatedData.kpi.yearlyJoined) }}</td>
                    <td v-for="(m, i) in calculatedData.members" :key="'join-' + i" class="text-right text-success">{{ formatMoney(m.joined) }}</td>
                  </tr>
                  <tr>
                    <td class="header-col pl-4 text-error">Abgänge (vertragswirksam)</td>
                    <td class="text-right info-col text-error annual-col">{{ formatMoney(calculatedData.kpi.yearlyLeft) }}</td>
                    <td v-for="(m, i) in calculatedData.members" :key="'left-' + i" class="text-right text-error">{{ formatMoney(m.left) }}</td>
                  </tr>
                  <tr class="font-weight-bold highlight-row">
                    <td class="header-col pl-4">Stand Ende des Monats</td>
                    <td class="text-right info-col annual-col"></td>
                    <td v-for="(m, i) in calculatedData.members" :key="'end-' + i" class="text-right">{{ formatMoney(m.end) }}</td>
                  </tr>

                  <tr>
                    <td class="header-col pl-4">Ruhend</td>
                    <td class="text-right info-col annual-col"></td>
                    <td v-for="(m, i) in calculatedData.members" :key="'pause-' + i" class="text-right">{{ formatMoney(m.paused) }}</td>
                  </tr>
                  <tr>
                    <td class="header-col pl-4">Ruhenden Quote</td>
                    <td class="text-right info-col annual-col">{{ formatPercent(calculatedData.kpi.yearlyPausedRate) }}</td>
                    <td v-for="(m, i) in calculatedData.members" :key="'pauseRate-' + i" class="text-right">{{ formatPercent(m.pausedRate) }}</td>
                  </tr>
                  <tr>
                    <td class="header-col pl-4">Fluktuationsquote</td>
                    <td class="text-right info-col annual-col">{{ formatPercent(calculatedData.kpi.yearlyFluctuation) }}</td>
                    <td v-for="(m, i) in calculatedData.members" :key="'fluc-' + i" class="text-right">{{ formatPercent(m.fluctuation) }}</td>
                  </tr>
                  <tr>
                    <td class="header-col pl-4">Wachstum</td>
                    <td class="text-right info-col annual-col">{{ formatPercent(calculatedData.kpi.yearlyGrowthRate) }}</td>
                    <td v-for="(m, i) in calculatedData.members" :key="'growth-' + i" class="text-right">{{ formatPercent(m.growth) }}</td>
                  </tr>
                  <tr class="highlight-row">
                    <td class="header-col pl-4">Wachstum absolut</td>
                    <td
                      class="text-right info-col font-weight-bold annual-col"
                      :class="calculatedData.kpi.yearlyGrowth < 0 ? 'text-error' : 'text-success'">
                      {{ calculatedData.kpi.yearlyGrowth > 0 ? "+" : "" }}{{ formatMoney(calculatedData.kpi.yearlyGrowth) }}
                    </td>
                    <td
                      v-for="(m, i) in calculatedData.members"
                      :key="'abs-' + i"
                      class="text-right"
                      :class="m.absGrowth < 0 ? 'text-error' : 'text-success'">
                      {{ m.absGrowth > 0 ? "+" : "" }}{{ formatMoney(m.absGrowth) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>

          <!-- Umsatz-Planung -->
          <v-card outlined class="mb-6 calculated-card" dark>
            <v-simple-table dense dark class="calc-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left font-weight-bold header-col primary--text text--lighten-1" style="font-size: 14px !important">
                      Umsatz-Planung
                    </th>
                    <th class="text-right info-col" width="100">Pro Mitglied</th>
                    <th class="text-right info-col" width="100">Steigerung p.a. in %</th>
                    <th class="text-right info-col" width="100">% vom Umsatz</th>
                    <th class="text-right info-col annual-col" width="100">Jahreswert</th>
                    <th v-for="m in months" :key="m.key" class="text-right" width="80">{{ m.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in revenueItems" :key="'rev-' + item.key">
                    <td class="header-col pl-4">{{ item.label }}</td>
                    <td class="text-right info-col">
                      {{ calculatedData.revenueAvgPerMember[item.key] ? calculatedData.revenueAvgPerMember[item.key].toFixed(1) : "" }}
                    </td>
                    <td class="text-right info-col">
                      {{
                        formatPercent(
                          (planData.revenue.growthRates && planData.revenue.growthRates[item.key] ? planData.revenue.growthRates[item.key] : 0) / 100,
                        )
                      }}
                    </td>
                    <td class="text-right info-col">
                      {{ formatPercent(calculatedData.revenueYearly[item.key] / calculatedData.kpi.yearlyTotalRevenue) }}
                    </td>
                    <td class="text-right info-col annual-col">{{ formatMoney(calculatedData.revenueYearly[item.key]) }}</td>
                    <td v-for="(val, i) in calculatedData.revenue[item.key]" :key="'rev-val-' + i" class="text-right">{{ formatMoney(val) }}</td>
                  </tr>
                  <tr class="font-weight-black highlight-row total-row">
                    <td class="header-col pl-4">GESAMT ERLÖSE</td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col annual-col">{{ formatMoney(calculatedData.kpi.yearlyTotalRevenue) }}</td>
                    <td v-for="(val, i) in calculatedData.kpi.gesamtErloese" :key="'tot-rev-' + i" class="text-right">{{ formatMoney(val) }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>

          <!-- Kosten-Planung -->
          <v-card outlined class="mb-6 calculated-card" dark>
            <v-simple-table dense dark class="calc-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left font-weight-bold header-col primary--text text--lighten-1" style="font-size: 14px !important">
                      Kosten-Planung
                    </th>
                    <th class="text-right info-col" width="100">pro Monat</th>
                    <th class="text-right info-col" width="100">Steigerung p.a. in %</th>
                    <th class="text-right info-col" width="100">% vom Umsatz</th>
                    <th class="text-right info-col annual-col" width="100">Jahreswert</th>
                    <th v-for="m in months" :key="m.key" class="text-right" width="80">{{ m.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="header-col pl-4">{{ costItems.find((c) => c.key === "cogs").label }}</td>
                    <td class="text-right info-col">{{ formatMoney(calculatedData.costsMonthly["cogs"]) }}</td>
                    <td class="text-right info-col">
                      {{
                        formatPercent(
                          (planData.costs.growthRates && planData.costs.growthRates["cogs"] ? planData.costs.growthRates["cogs"] : 0) / 100,
                        )
                      }}
                    </td>
                    <td class="text-right info-col">
                      {{ formatPercent(calculatedData.costsYearly["cogs"] / calculatedData.kpi.yearlyTotalRevenue) }}
                    </td>
                    <td class="text-right info-col annual-col">{{ formatMoney(calculatedData.costsYearly["cogs"]) }}</td>
                    <td v-for="(val, i) in calculatedData.costs['cogs']" :key="'cogs-' + i" class="text-right">{{ formatMoney(val) }}</td>
                  </tr>
                  <tr class="font-weight-bold highlight-row">
                    <td class="header-col pl-4">Rohertrag</td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col annual-col">
                      {{ formatMoney(calculatedData.kpi.yearlyTotalRevenue - calculatedData.costsYearly["cogs"]) }}
                    </td>
                    <td v-for="(val, i) in calculatedData.kpi.rohertrag" :key="'rohe-' + i" class="text-right">{{ formatMoney(val) }}</td>
                  </tr>

                  <!-- OPEX Costs -->
                  <tr v-for="item in opexItems" :key="'opex-' + item.key">
                    <td class="header-col pl-4">{{ item.label }}</td>
                    <td class="text-right info-col">{{ formatMoney(calculatedData.costsMonthly[item.key]) }}</td>
                    <td class="text-right info-col">
                      {{
                        formatPercent(
                          ((planData.costs.growthRates &&
                            (item.isNested ? planData.costs.growthRates[item.isNested][item.nestedKey] : planData.costs.growthRates[item.key])) ||
                            0) / 100,
                        )
                      }}
                    </td>
                    <td class="text-right info-col">
                      {{ formatPercent(calculatedData.costsYearly[item.key] / calculatedData.kpi.yearlyTotalRevenue) }}
                    </td>
                    <td class="text-right info-col annual-col">{{ formatMoney(calculatedData.costsYearly[item.key]) }}</td>
                    <td v-for="(val, i) in calculatedData.costs[item.key]" :key="'opex-val-' + i" class="text-right">{{ formatMoney(val) }}</td>
                  </tr>

                  <tr class="font-weight-bold highlight-row text-error">
                    <td class="header-col pl-4">Gesamtkosten</td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col">{{ formatPercent(calculatedData.kpi.yearlyOpex / calculatedData.kpi.yearlyTotalRevenue) }}</td>
                    <td class="text-right info-col annual-col">{{ formatMoney(calculatedData.kpi.yearlyOpex) }}</td>
                    <td v-for="(val, i) in calculatedData.kpi.gesamtkosten" :key="'tot-cost-' + i" class="text-right">{{ formatMoney(val) }}</td>
                  </tr>

                  <tr class="font-weight-black highlight-row total-row">
                    <td class="header-col pl-4">EBIT DA</td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col annual-col" :class="calculatedData.kpi.yearlyEbitda < 0 ? 'text-error' : ''">
                      {{ formatMoney(calculatedData.kpi.yearlyEbitda) }}
                    </td>
                    <td v-for="(val, i) in calculatedData.kpi.ebitda" :key="'ebitda-' + i" class="text-right" :class="val < 0 ? 'text-error' : ''">
                      {{ formatMoney(val) }}
                    </td>
                  </tr>

                  <tr>
                    <td class="header-col pl-4">{{ costItems.find((c) => c.key === "depreciation").label }}</td>
                    <td class="text-right info-col">{{ formatMoney(calculatedData.costsMonthly["depreciation"]) }}</td>
                    <td class="text-right info-col">
                      {{
                        formatPercent(
                          (planData.costs.growthRates && planData.costs.growthRates["depreciation"]
                            ? planData.costs.growthRates["depreciation"]
                            : 0) / 100,
                        )
                      }}
                    </td>
                    <td class="text-right info-col">
                      {{ formatPercent(calculatedData.costsYearly["depreciation"] / calculatedData.kpi.yearlyTotalRevenue) }}
                    </td>
                    <td class="text-right info-col annual-col">{{ formatMoney(calculatedData.costsYearly["depreciation"]) }}</td>
                    <td v-for="(val, i) in calculatedData.costs['depreciation']" :key="'depr-' + i" class="text-right">{{ formatMoney(val) }}</td>
                  </tr>

                  <tr class="font-weight-black highlight-row total-row">
                    <td class="header-col pl-4">EBIT</td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col annual-col" :class="calculatedData.kpi.yearlyEbit < 0 ? 'text-error' : ''">
                      {{ formatMoney(calculatedData.kpi.yearlyEbit) }}
                    </td>
                    <td v-for="(val, i) in calculatedData.kpi.ebit" :key="'ebit-' + i" class="text-right" :class="val < 0 ? 'text-error' : ''">
                      {{ formatMoney(val) }}
                    </td>
                  </tr>

                  <!-- Non OPEX Items -->
                  <tr v-for="item in nonOpexItems" :key="'nonopex-' + item.key">
                    <td class="header-col pl-4">{{ item.label }}</td>
                    <td class="text-right info-col">{{ formatMoney(calculatedData.costsMonthly[item.key]) }}</td>
                    <td class="text-right info-col">
                      {{
                        formatPercent(
                          (planData.costs.growthRates && planData.costs.growthRates[item.key] ? planData.costs.growthRates[item.key] : 0) / 100,
                        )
                      }}
                    </td>
                    <td class="text-right info-col">
                      {{ formatPercent(calculatedData.costsYearly[item.key] / calculatedData.kpi.yearlyTotalRevenue) }}
                    </td>
                    <td class="text-right info-col annual-col">{{ formatMoney(calculatedData.costsYearly[item.key]) }}</td>
                    <td v-for="(val, i) in calculatedData.costs[item.key]" :key="'nonopex-val-' + i" class="text-right">{{ formatMoney(val) }}</td>
                  </tr>

                  <tr class="font-weight-black highlight-row result-row">
                    <td class="header-col pl-4">Ergebnis nach Steuern</td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col"></td>
                    <td class="text-right info-col annual-col" :class="calculatedData.kpi.yearlyErgebnis < 0 ? 'text-error' : 'text-success'">
                      {{ formatMoney(calculatedData.kpi.yearlyErgebnis) }}
                    </td>
                    <td
                      v-for="(val, i) in calculatedData.kpi.ergebnisNachSteuern"
                      :key="'ergebnis-' + i"
                      class="text-right"
                      :class="val < 0 ? 'text-error' : 'text-success'">
                      {{ formatMoney(val) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </div>
      </v-tab-item>
    </v-tabs-items>
  </v-container>
</template>

<script>
import api from "@/api"
import { mapState, mapActions, mapGetters } from "vuex"

function getDefaultMonthly() {
  return { jan: 0, feb: 0, mar: 0, apr: 0, may: 0, jun: 0, jul: 0, aug: 0, sep: 0, oct: 0, nov: 0, dec: 0 }
}

function getDefaultPlan() {
  return {
    studioId: null,
    year: "2026",
    members: {
      baseJan: 0,
      joined: getDefaultMonthly(),
      left: getDefaultMonthly(),
      pausedRate: getDefaultMonthly(), // Now it is percentage based (e.g. 5 means 5%)
    },
    revenue: {
      dues: 0,
      startupFees: 0,
      bistro: 0,
      medical: 0,
      nutrition: 0,
      shop: 0,
      solarium: 0,
      childcare: 0,
      squash: 0,
      physio: 0,
      massage: 0,
      rental: 0,
      ads: 0,
      debtCollection: 0,
      solarPower: 0,
      other: 0,
      growthRates: {
        dues: 0,
        startupFees: 0,
        bistro: 0,
        medical: 0,
        nutrition: 0,
        shop: 0,
        solarium: 0,
        childcare: 0,
        squash: 0,
        physio: 0,
        massage: 0,
        rental: 0,
        ads: 0,
        debtCollection: 0,
        solarPower: 0,
        other: 0,
      },
      extraMonthly: {
        dues: getDefaultMonthly(),
        startupFees: getDefaultMonthly(),
        bistro: getDefaultMonthly(),
        medical: getDefaultMonthly(),
        nutrition: getDefaultMonthly(),
        shop: getDefaultMonthly(),
        solarium: getDefaultMonthly(),
        childcare: getDefaultMonthly(),
        squash: getDefaultMonthly(),
        physio: getDefaultMonthly(),
        massage: getDefaultMonthly(),
        rental: getDefaultMonthly(),
        ads: getDefaultMonthly(),
        debtCollection: getDefaultMonthly(),
        solarPower: getDefaultMonthly(),
        other: getDefaultMonthly(),
      },
    },
    costs: {
      cogs: 0,
      personnel: { thirdParty: 0, wages: 0 },
      rent: 0,
      energy: 0,
      insuranceFee: 0,
      vehicles: 0,
      marketing: 0,
      travel: 0,
      maintenance: 0,
      admin: 0,
      it: 0,
      consulting: 0,
      licenses: 0,
      leasing: 0,
      otherOps: 0,
      badDebt: 0,
      depreciation: 0,
      financeIncome: 0,
      financeCost: 0,
      neutralCost: 0,
      neutralIncome: 0,
      taxes: 0,
      growthRates: {
        cogs: 0,
        personnel: { thirdParty: 0, wages: 0 },
        rent: 0,
        energy: 0,
        insuranceFee: 0,
        vehicles: 0,
        marketing: 0,
        travel: 0,
        maintenance: 0,
        admin: 0,
        it: 0,
        consulting: 0,
        licenses: 0,
        leasing: 0,
        otherOps: 0,
        badDebt: 0,
        depreciation: 0,
        financeIncome: 0,
        financeCost: 0,
        neutralCost: 0,
        neutralIncome: 0,
        taxes: 0,
      },
      extraMonthly: {
        cogs: getDefaultMonthly(),
        personnel: { thirdParty: getDefaultMonthly(), wages: getDefaultMonthly() },
        rent: getDefaultMonthly(),
        energy: getDefaultMonthly(),
        insuranceFee: getDefaultMonthly(),
        vehicles: getDefaultMonthly(),
        marketing: getDefaultMonthly(),
        travel: getDefaultMonthly(),
        maintenance: getDefaultMonthly(),
        admin: getDefaultMonthly(),
        it: getDefaultMonthly(),
        consulting: getDefaultMonthly(),
        licenses: getDefaultMonthly(),
        leasing: getDefaultMonthly(),
        otherOps: getDefaultMonthly(),
        badDebt: getDefaultMonthly(),
        depreciation: getDefaultMonthly(),
        financeIncome: getDefaultMonthly(),
        financeCost: getDefaultMonthly(),
        neutralCost: getDefaultMonthly(),
        neutralIncome: getDefaultMonthly(),
        taxes: getDefaultMonthly(),
      },
    },
  }
}

export default {
  name: "PlanningMain",
  data() {
    return {
      activeTab: 0,
      viewMode: "input",
      isEditing: false,
      isSaving: false,
      masterYear: "2026",
      availableYears: ["2025", "2026", "2027", "2028"],
      allPlans: [],

      months: [
        { key: "jan", label: "Jan" },
        { key: "feb", label: "Feb" },
        { key: "mar", label: "Mrz" },
        { key: "apr", label: "Apr" },
        { key: "may", label: "Mai" },
        { key: "jun", label: "Jun" },
        { key: "jul", label: "Jul" },
        { key: "aug", label: "Aug" },
        { key: "sep", label: "Sep" },
        { key: "oct", label: "Okt" },
        { key: "nov", label: "Nov" },
        { key: "dec", label: "Dez" },
      ],

      // Definition der dynamischen Felder für die Umsatz-Tabelle
      revenueItems: [
        { key: "dues", label: "Mitgliedsbeiträge" },
        { key: "startupFees", label: "Startpakete / Gebühren" },
        { key: "bistro", label: "Bistro" },
        { key: "medical", label: "Kurse / Tests / Arzt" },
        { key: "nutrition", label: "VK Ernährung" },
        { key: "shop", label: "VK Shop" },
        { key: "solarium", label: "Solarium" },
        { key: "childcare", label: "Kinderhort" },
        { key: "squash", label: "Squash" },
        { key: "physio", label: "Physiotherapie" },
        { key: "massage", label: "Entspannung / Massage" },
        { key: "rental", label: "Vermietung" },
        { key: "ads", label: "TV / Werbung" },
        { key: "debtCollection", label: "Ertrag Betreibungskosten" },
        { key: "solarPower", label: "PV-Strom Ertrag" },
        { key: "other", label: "Übriger Ertrag" },
      ],

      // Definition der dynamischen Felder für die Kosten-Tabelle
      costItems: [
        { key: "cogs", label: "Wareneinsatz" },
        { key: "wages", label: "Personalaufwand", isNested: "personnel", nestedKey: "wages" },
        { key: "thirdParty", label: "Leistungen Dritter", isNested: "personnel", nestedKey: "thirdParty" },
        { key: "rent", label: "Raumaufwand" },
        { key: "energy", label: "Energie- und Entsorgungsaufwand" },
        { key: "insuranceFee", label: "Sachversicherungen, Abgaben" },
        { key: "vehicles", label: "Fahrzeug- und Transportaufwand" },
        { key: "marketing", label: "Werbeaufwand" },
        { key: "travel", label: "Reisespesen / Kundenbetreuung" },
        { key: "maintenance", label: "Reparaturen / Wartungen" },
        { key: "admin", label: "Verwaltungsaufwand" },
        { key: "it", label: "Informatikaufwand" },
        { key: "consulting", label: "Beratungskosten" },
        { key: "licenses", label: "Lizenzen" },
        { key: "leasing", label: "Leasing" },
        { key: "otherOps", label: "Übriger Betriebsaufwand" },
        { key: "badDebt", label: "Forderungsverluste" },
        { key: "depreciation", label: "Abschreibungen" },
        { key: "financeCost", label: "Finanzaufwand" },
        { key: "financeIncome", label: "Finanzerträge" },
        { key: "neutralCost", label: "Neutraler Aufwand" },
        { key: "neutralIncome", label: "Neutraler Ertrag" },
        { key: "taxes", label: "Betriebliche Steuern" },
      ],

      planData: getDefaultPlan(),
      actualData: null,

      editMonthlyDialog: {
        isOpen: false,
        key: null,
        isNested: null,
        nestedKey: null,
        type: null, // "revenue" or "costs"
        label: "",
      },
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    calculatedActuals() {
      const c = {
        costs: {},
        costsYearly: {},
        kpi: {
          gesamtkosten: new Array(12).fill(0),
          yearlyOpex: 0,
        },
      }

      if (!this.actualData || !this.actualData.costs) return c

      const nonOpexKeys = ["cogs", "depreciation", "financeCost", "financeIncome", "neutralCost", "neutralIncome", "taxes"]

      this.costItems.forEach((item) => {
        const monthly = this.actualData.costs[item.key] || new Array(12).fill(0)
        c.costs[item.key] = monthly
        c.costsYearly[item.key] = monthly.reduce((sum, val) => sum + val, 0)

        monthly.forEach((val, i) => {
          if (item.key !== "cogs" && !nonOpexKeys.includes(item.key)) {
            c.kpi.gesamtkosten[i] += val
          }
        })
      })

      c.kpi.yearlyOpex = c.kpi.gesamtkosten.reduce((sum, val) => sum + val, 0)
      return c
    },
    opexItems() {
      const nonOpex = ["cogs", "depreciation", "financeCost", "financeIncome", "neutralCost", "neutralIncome", "taxes"]
      return this.costItems.filter((c) => !nonOpex.includes(c.key))
    },
    nonOpexItems() {
      const list = ["financeIncome", "financeCost", "neutralCost", "neutralIncome", "taxes"]
      return this.costItems.filter((c) => list.includes(c.key))
    },
    calculatedData() {
      const c = {
        members: [],
        revenue: {},
        revenueYearly: {},
        revenueAvgPerMember: {},
        costs: {},
        costsYearly: {},
        costsMonthly: {},
        kpi: {
          gesamtErloese: [],
          rohertrag: [],
          gesamtkosten: [],
          ebitda: [],
          ebit: [],
          ergebnisNachSteuern: [],
          yearlyJoined: 0,
          yearlyLeft: 0,
          yearlyGrowth: 0,
          yearlyTotalRevenue: 0,
          yearlyOpex: 0,
          yearlyEbitda: 0,
          yearlyEbit: 0,
          yearlyErgebnis: 0,
          yearlyPausedRate: 0,
          yearlyFluctuation: 0,
          yearlyGrowthRate: 0,
        },
      }

      if (!this.planData) return c
      const pd = this.planData

      let currentMembers = pd.members.baseJan || 0
      let totalMembersYear_End = 0
      let totalMembersYear_Start = 0
      let totalJoinedYear = 0

      // 1. Members
      for (const m of this.months) {
        const joined = pd.members.joined[m.key] || 0
        const left = pd.members.left[m.key] || 0
        const pausedRate = (pd.members.pausedRate && pd.members.pausedRate[m.key]) || 0 // Note: this is a percentage like 5 for 5%

        const endMembers = currentMembers + joined - left
        const fluctuation = currentMembers > 0 ? (left / currentMembers) * 12 : 0
        const growth = currentMembers > 0 ? (joined - left) / currentMembers : 0
        const paused = endMembers * (pausedRate / 100)

        c.members.push({
          start: currentMembers,
          joined,
          left,
          end: endMembers,
          paused,
          pausedRate: pausedRate / 100, // store as decimal for formatPercent
          fluctuation,
          growth,
          absGrowth: joined - left,
        })

        totalMembersYear_Start += currentMembers
        totalMembersYear_End += endMembers
        totalJoinedYear += joined
        c.kpi.yearlyJoined += joined
        c.kpi.yearlyLeft += left
        c.kpi.yearlyGrowth += joined - left
        currentMembers = endMembers
      }

      const yearlyBaseJan = pd.members.baseJan || 0
      if (yearlyBaseJan > 0) {
        c.kpi.yearlyFluctuation = c.kpi.yearlyLeft / yearlyBaseJan
        c.kpi.yearlyGrowthRate = c.kpi.yearlyGrowth / yearlyBaseJan
      }
      if (totalMembersYear_End > 0) {
        // Average paused rate across the year
        const totalPaused = c.members.reduce((sum, m) => sum + m.paused, 0)
        c.kpi.yearlyPausedRate = totalPaused / totalMembersYear_End
      }

      const distributeByStartMembers = (proMitglied, growthRate, deductPaused) => {
        return this.months.map((m, i) => {
          const membersToCount = deductPaused ? c.members[i].start * (1 - c.members[i].pausedRate) : c.members[i].start
          return proMitglied * membersToCount * (1 + (growthRate / 100) * ((i + 1) / 12))
        })
      }
      const distributeByJoined = (proStartpaket, growthRate) => {
        return this.months.map((m, i) => proStartpaket * c.members[i].joined * (1 + (growthRate / 100) * ((i + 1) / 12)))
      }
      const distributeFlat = (monthlyVal, growthRate) => {
        return this.months.map((m, i) => monthlyVal * (1 + (growthRate / 100) * ((i + 1) / 12)))
      }

      // 2. Revenues
      const revMemberKeys = ["dues", "bistro", "nutrition", "shop", "childcare"]
      const revJoinedKeys = ["startupFees"]
      const revMonthly = new Array(12).fill(0)

      this.revenueItems.forEach((item) => {
        const inputVal = pd.revenue[item.key] || 0
        const growthRate = pd.revenue.growthRates ? pd.revenue.growthRates[item.key] || 0 : 0

        let dist = []
        if (revMemberKeys.includes(item.key)) {
          const deductPaused = item.key === "dues" // Only 'dues' deducts paused members according to user logic
          dist = distributeByStartMembers(inputVal, growthRate, deductPaused)
          c.revenueAvgPerMember[item.key] = inputVal
        } else if (revJoinedKeys.includes(item.key)) {
          dist = distributeByJoined(inputVal, growthRate)
          c.revenueAvgPerMember[item.key] = inputVal
        } else {
          dist = distributeFlat(inputVal, growthRate)
          c.revenueAvgPerMember[item.key] = inputVal // Technically not 'per member', but shows the base input value
        }

        // Add specific extra monthly amounts if set
        if (pd.revenue.extraMonthly && pd.revenue.extraMonthly[item.key]) {
          dist = dist.map((val, i) => val + (pd.revenue.extraMonthly[item.key][this.months[i].key] || 0))
        }

        c.revenue[item.key] = dist

        const yearlyTotal = dist.reduce((sum, val) => sum + val, 0)
        c.revenueYearly[item.key] = yearlyTotal
        c.kpi.yearlyTotalRevenue += yearlyTotal
        dist.forEach((val, i) => {
          revMonthly[i] += val
        })
      })

      // 3. Costs
      const costMonthlyArr = new Array(12).fill(0)
      const cogsMonthly = new Array(12).fill(0)

      const nonOpexKeys = ["cogs", "depreciation", "financeCost", "financeIncome", "neutralCost", "neutralIncome", "taxes"]

      this.costItems.forEach((item) => {
        let monthlyVal = 0
        let growthRate = 0
        let extraMonthlyObj = null

        if (item.isNested) {
          monthlyVal = pd.costs[item.isNested]?.[item.nestedKey] || 0
          growthRate = pd.costs.growthRates?.[item.isNested]?.[item.nestedKey] || 0
          extraMonthlyObj = pd.costs.extraMonthly?.[item.isNested]?.[item.nestedKey] || null
        } else {
          monthlyVal = pd.costs[item.key] || 0
          growthRate = pd.costs.growthRates?.[item.key] || 0
          extraMonthlyObj = pd.costs.extraMonthly?.[item.key] || null
        }

        let dist = this.months.map((m, i) => monthlyVal * (1 + (growthRate / 100) * ((i + 1) / 12)))

        if (extraMonthlyObj) {
          dist = dist.map((val, i) => val + (extraMonthlyObj[this.months[i].key] || 0))
        }

        const yearlyCostVal = dist.reduce((sum, val) => sum + val, 0)

        c.costsMonthly[item.key] = monthlyVal
        c.costsYearly[item.key] = yearlyCostVal
        c.costs[item.key] = dist

        dist.forEach((val, i) => {
          if (item.key === "cogs") {
            cogsMonthly[i] += val
          } else if (!nonOpexKeys.includes(item.key)) {
            costMonthlyArr[i] += val
          }
        })
      })

      // 4. KPIs
      for (let i = 0; i < 12; i++) {
        const erloese = revMonthly[i]
        const cogs = cogsMonthly[i]
        const rohertrag = erloese - cogs
        const opex = costMonthlyArr[i]
        const ebitda = rohertrag - opex
        const depr = c.costs["depreciation"][i] || 0
        const ebit = ebitda - depr

        const finInc = c.costs["financeIncome"][i] || 0
        const finCost = c.costs["financeCost"][i] || 0
        const neutInc = c.costs["neutralIncome"][i] || 0
        const neutCost = c.costs["neutralCost"][i] || 0
        const ebt = ebit + finInc - finCost + neutInc - neutCost

        const taxes = c.costs["taxes"][i] || 0
        const nachSteuern = ebt - taxes

        c.kpi.gesamtErloese.push(erloese)
        c.kpi.rohertrag.push(rohertrag)
        c.kpi.gesamtkosten.push(opex)
        c.kpi.ebitda.push(ebitda)
        c.kpi.ebit.push(ebit)
        c.kpi.ergebnisNachSteuern.push(nachSteuern)

        c.kpi.yearlyOpex += opex
        c.kpi.yearlyEbitda += ebitda
        c.kpi.yearlyEbit += ebit
        c.kpi.yearlyErgebnis += nachSteuern
      }

      return c
    },
    mappedStudios() {
      return this.storeStudios
        .filter((studio) => studio.code && studio.code !== "ot" && studio.code !== "alibey")
        .map((studio) => {
          return { _id: studio._id, name: studio.name }
        })
    },
    aggregatedPlan() {
      if (!this.allPlans.length) return { totalRevenue: 0, totalCosts: 0, totalJoined: 0 }

      return this.allPlans.reduce(
        (acc, plan) => {
          // Addiert alle relevanten Umsatzfelder (ohne GrowthRate)
          const revKeys = Object.keys(plan.revenue || {}).filter((k) => k !== "growthRates")
          const revTotal = revKeys.reduce((sum, key) => sum + (plan.revenue[key] || 0), 0)

          // Addiert alle relevanten Kostenfelder
          const costKeys = Object.keys(plan.costs || {}).filter((k) => k !== "personnel" && k !== "financeIncome" && k !== "neutralIncome")
          let costTotal = costKeys.reduce((sum, key) => sum + (plan.costs[key] || 0), 0)

          // Personalaufwand dazurechnen
          if (plan.costs.personnel) {
            costTotal += (plan.costs.personnel.wages || 0) + (plan.costs.personnel.thirdParty || 0)
          }
          // Erträge in der Kostenstruktur abziehen
          costTotal -= (plan.costs.financeIncome || 0) + (plan.costs.neutralIncome || 0)

          let joinedTotal = 0
          if (plan.members && plan.members.joined) {
            joinedTotal = Object.values(plan.members.joined).reduce((sum, val) => sum + (val || 0), 0)
          }

          acc.totalRevenue += revTotal
          acc.totalCosts += costTotal
          acc.totalJoined += joinedTotal
          return acc
        },
        { totalRevenue: 0, totalCosts: 0, totalJoined: 0 },
      )
    },
  },
  methods: {
    // --- NEUE HELPER-METHODEN FÜR DAS DELTA ---
    getDeltaClass(ist, plan, isCost = true) {
      const diff = ist - plan
      if (diff === 0) return "grey--text"
      if (isCost) {
        return diff > 0 ? "text-error" : "text-success" // Bei Kosten: Über Plan = rot, Unter Plan = grün
      }
      return diff > 0 ? "text-success" : "text-error"
    },
    formatDelta(ist, plan) {
      const diff = ist - plan
      if (diff === 0) return ""
      const prefix = diff > 0 ? "+" : ""
      return prefix + this.formatMoney(diff)
    },
    formatNumber(val) {
      return new Intl.NumberFormat("de-CH").format(val)
    },
    formatMoney(val) {
      if (val === undefined || val === null) return "0"
      return new Intl.NumberFormat("de-CH", { maximumFractionDigits: 0 }).format(Math.round(val))
    },
    formatPercent(val) {
      if (val === undefined || val === null) return "0.0%"
      return (val * 100).toFixed(1) + "%"
    },
    async loadAllPlans() {
      try {
        this.allPlans = await api.planning.fetchPlans({ year: this.masterYear })
      } catch (error) {
        console.error("Fehler beim Laden der Gesamtpläne:", error)
      }
    },
    async loadLocationPlan() {
      try {
        let studioId = this.planData.studioId
        if (this.planData.studioId && typeof this.planData.studioId === "object" && this.planData.studioId._id) {
          studioId = this.planData.studioId._id
        }
        // BEIDES PARALLEL LADEN (Plan und Ist)
        const [plans, actuals] = await Promise.all([
          api.planning.fetchPlans({ studioId, year: this.planData.year }),
          api.planning.fetchActuals({ studioId, year: this.planData.year }).catch(() => null),
        ])

        this.actualData = actuals // Ist-Zahlen speichern

        if (plans && plans.length > 0) {
          const fetchedPlan = plans[0]
          const defaultPlan = getDefaultPlan()
          this.planData = {
            ...defaultPlan,
            ...fetchedPlan,
            members: {
              ...defaultPlan.members,
              ...(fetchedPlan.members || {}),
              // Ensure joined, left, pausedRate exist
              joined: { ...defaultPlan.members.joined, ...(fetchedPlan.members?.joined || {}) },
              left: { ...defaultPlan.members.left, ...(fetchedPlan.members?.left || {}) },
              pausedRate: { ...defaultPlan.members.pausedRate, ...(fetchedPlan.members?.pausedRate || {}) },
            },
            revenue: {
              ...defaultPlan.revenue,
              ...(fetchedPlan.revenue || {}),
              growthRates: {
                ...defaultPlan.revenue.growthRates,
                ...(fetchedPlan.revenue?.growthRates || {}),
              },
              extraMonthly: {
                ...defaultPlan.revenue.extraMonthly,
                ...(fetchedPlan.revenue?.extraMonthly || {}),
              },
            },
            costs: {
              ...defaultPlan.costs,
              ...(fetchedPlan.costs || {}),
              personnel: {
                ...defaultPlan.costs.personnel,
                ...(fetchedPlan.costs?.personnel || {}),
              },
              growthRates: {
                ...defaultPlan.costs.growthRates,
                ...(fetchedPlan.costs?.growthRates || {}),
                personnel: {
                  ...defaultPlan.costs.growthRates.personnel,
                  ...(fetchedPlan.costs?.growthRates?.personnel || {}),
                },
              },
              extraMonthly: {
                ...defaultPlan.costs.extraMonthly,
                ...(fetchedPlan.costs?.extraMonthly || {}),
                personnel: {
                  ...defaultPlan.costs.extraMonthly.personnel,
                  ...(fetchedPlan.costs?.extraMonthly?.personnel || {}),
                },
              },
            },
          }
        } else {
          const sid = this.planData.studioId
          const y = this.planData.year
          this.planData = getDefaultPlan()
          this.planData.studioId = sid
          this.planData.year = y
        }
        this.isEditing = true
      } catch (error) {
        console.error("Fehler beim Laden des Standortplans:", error)
      }
    },
    async savePlan() {
      this.isSaving = true
      try {
        const dataToSave = JSON.parse(JSON.stringify(this.planData))
        if (dataToSave.studioId && typeof dataToSave.studioId === "object" && dataToSave.studioId._id) {
          dataToSave.studioId = dataToSave.studioId._id
        }

        await api.planning.savePlan(dataToSave)
        this.loadAllPlans()
      } catch (error) {
        console.error("Fehler beim Speichern:", error)
      } finally {
        this.isSaving = false
      }
    },
    openMonthlyDialog(type, item) {
      this.editMonthlyDialog.type = type
      this.editMonthlyDialog.key = item.key
      this.editMonthlyDialog.isNested = item.isNested || null
      this.editMonthlyDialog.nestedKey = item.nestedKey || null
      this.editMonthlyDialog.label = item.label
      this.editMonthlyDialog.isOpen = true
    },
    getEditTarget() {
      const type = this.editMonthlyDialog.type
      const key = this.editMonthlyDialog.key
      const isNested = this.editMonthlyDialog.isNested
      const nestedKey = this.editMonthlyDialog.nestedKey

      if (!type || !key) return {}

      if (isNested && nestedKey) {
        return this.planData[type].extraMonthly[isNested][nestedKey]
      } else {
        return this.planData[type].extraMonthly[key]
      }
    },
    hasExtraMonthly(type, item) {
      if (!this.planData || !this.planData[type] || !this.planData[type].extraMonthly) return false

      let target
      if (item.isNested && item.nestedKey) {
        if (!this.planData[type].extraMonthly[item.isNested]) return false
        target = this.planData[type].extraMonthly[item.isNested][item.nestedKey]
      } else {
        target = this.planData[type].extraMonthly[item.key]
      }

      if (!target) return false

      return Object.values(target).some((val) => val && val !== 0)
    },
  },
  mounted() {
    this.loadAllPlans()
  },
}
</script>

<style scoped>
/* Transparente Hintergründe für die Tabellen-Cards, damit das Dark Theme von der v-card wirkt */
.transparent-table {
  background-color: transparent !important;
}

/* Reduziertes Padding für maximale Übersichtlichkeit */
.v-data-table td,
.v-data-table th {
  padding: 0 8px !important;
  height: 40px !important;
}

/* Text-Fields in der Tabelle extrem kompakt machen */
.compact-input {
  margin: 0 !important;
  padding: 0 !important;
  font-size: 14px;
}
.compact-input >>> .v-input__control > .v-input__slot {
  min-height: 32px !important;
}
.compact-input >>> .v-text-field__details {
  display: none !important;
}

/* Berechnete Ansicht Styles */
.calculated-view .calculated-card {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3) !important;
}

.calc-table {
  background-color: #1e1e1e !important;
}

.calc-table .v-data-table__wrapper > table > tbody > tr > td {
  font-size: 13px !important;
  height: 32px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
}

.calc-table .v-data-table__wrapper > table > thead > tr > th {
  font-size: 13px !important;
  border-bottom: 2px solid rgba(255, 255, 255, 0.1) !important;
  background-color: #2c2c2c !important;
}

.header-col {
  width: 250px;
  position: sticky;
  left: 0;
  background-color: #252525 !important;
  z-index: 2;
  border-right: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.info-col {
  width: 130px;
  background-color: rgba(0, 0, 0, 0.1) !important;
  border-right: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.calc-table .v-data-table__wrapper > table > thead > tr > th.info-col {
  background-color: #333 !important;
}

.annual-col {
  background-color: rgba(255, 255, 255, 0.04) !important;
  font-weight: 700 !important;
}

.calc-table .v-data-table__wrapper > table > tbody > tr.highlight-row > td {
  background-color: rgba(255, 255, 255, 0.03) !important;
}

.calc-table .v-data-table__wrapper > table > tbody > tr.total-row > td {
  background-color: rgba(255, 255, 255, 0.08) !important;
  border-top: 2px solid #555 !important;
  border-bottom: 2px solid #555 !important;
  font-weight: 900 !important;
  font-size: 14px !important;
  color: #fff !important;
}

.calc-table .v-data-table__wrapper > table > tbody > tr.result-row > td {
  background-color: rgba(25, 118, 210, 0.2) !important;
  border-top: 2px solid #1976d2 !important;
  border-bottom: 2px solid #1976d2 !important;
  font-weight: 900 !important;
  font-size: 14px !important;
  color: #fff !important;
}

/* Responsive fix für sticky col */
@media (max-width: 960px) {
  .header-col {
    position: relative;
    width: auto;
    background-color: transparent !important;
  }
}
</style>
